import { setFleetTodosForSession } from "./app-fleet-interaction.ts";
import type { OpenClawApp } from "./app.ts";
import {
  clearChatLastRunSnapshot,
  resolveChatLastRunSnapshot,
} from "./chat/chat-last-run-files.ts";
import {
  clearChatRunArtifactsBaseline,
  getChatRunArtifactsSinceTs,
} from "./chat/chat-run-artifacts.ts";
import {
  advanceComposerReview,
  collectArtifactScopeSessionKeys,
  dismissComposerFileReview,
} from "./chat/composer-file-review-actions.ts";
import { formatFileContextToken, formatFolderContextToken } from "./chat/file-context-picker.ts";
import { resolveAgentIdForSession } from "./chat/resolve-session-agent.ts";
import { withProgrammaticScroll } from "./chat/with-programmatic-scroll.ts";
import { loadChatHistory } from "./controllers/chat.ts";
import type { FleetTodoStatus } from "./controllers/fleet-todos.ts";
import { parseFleetTodoItems } from "./controllers/fleet-todos.ts";
import {
  fetchArtifactCheckpoints,
  revertArtifactChange,
  revertArtifactsSince,
  revertSnapshotFiles,
  type ArtifactCheckpoint,
} from "./controllers/session-artifacts-review.ts";
import { syncChatComposeDraft } from "./views/chat.ts";

// JAISIU-3473 — upward-pagination cursor.
//
// Initial chat open loads `HISTORY_PAGE` (200) latest messages. As the
// operator scrolls up toward the top, `loadOlderChatHistory` fetches the
// next older page using a server `before=<oldestMessageId>` cursor and
// prepends it. Scroll position is preserved with a per-row anchor
// (`data-msg-id`); the in-flight guard prevents duplicate requests from
// rapid scroll bursts. The pager is permanently disabled once
// `chatHistoryHasMoreOlder` flips false (server exhausted) or while a
// request is in flight. Programmatic scrollTop adjustment is bracketed
// by a `chatProgrammaticScroll` flag so the scroll handler does not
// classify the post-prepend viewport correction as a user gesture (which
// would otherwise arm the ↑/↓ chevrons at exactly the wrong moment).
const HISTORY_PAGE = 200;

/**
 * Anchor selector for the per-message DOM rows. Both the grouped-message
 * renderer (`grouped-render.ts`) and the assistant-turn renderer
 * (`chat-assistant-turn.ts`) emit `data-msg-id` on their outermost
 * wrapper so the scroll-preservation logic can locate the row that was
 * at the top of the viewport before a prepend and re-measure its
 * offsetTop after the new batch lands.
 */
export const CHAT_MSG_ID_SELECTOR = "[data-msg-id]";

/** Best-effort extraction of a stable id from a chat-history message. */
function getMessageId(message: unknown): string | undefined {
  if (!message || typeof message !== "object") return undefined;
  const record = message as { id?: unknown; transcriptId?: unknown; messageId?: unknown };
  for (const key of ["id", "transcriptId", "messageId"] as const) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  const inner = (record as { message?: { id?: unknown } }).message;
  if (inner && typeof inner === "object") {
    const innerId = (inner as { id?: unknown }).id;
    if (typeof innerId === "string" && innerId.trim()) {
      return innerId.trim();
    }
  }
  return undefined;
}

export type { ArtifactCheckpoint };

/**
 * Fetch the next-older page of chat history and prepend it to the
 * in-memory transcript. Scroll position is preserved by anchoring on
 * the topmost visible message at fetch time and re-measuring its
 * offsetTop after the prepend; the programmatic scrollTop adjustment
 * is bracketed by `chatProgrammaticScroll` so the scroll handler does
 * not misinterpret the post-prepend correction as a user gesture.
 *
 * Idempotent: a no-op when an older-load is already in flight, when
 * the server has reported no more older history, or when the cursor
 * is missing (cold start).
 */
export async function loadOlderChatHistory(app: OpenClawApp): Promise<void> {
  // JAISIU-3473 — guard set. Any of these short-circuits the call so
  // rapid scroll bursts never produce duplicate requests.
  if (app.chatHistoryLoadingOlder) return;
  if (!app.chatHistoryHasMoreOlder) return;
  const cursor = app.chatHistoryOldestLoadedId;
  if (!cursor) return;
  if (!app.client || !app.connected) return;

  // Snapshot the active sessionKey BEFORE any awaits so the post-await
  // race guard has a stable reference. If the operator switches chats
  // while the RPC is in flight, this snapshot still names the chat
  // the request was issued for.
  const requestedSessionKey = (app.sessionKey ?? "").trim();

  app.chatHistoryLoadingOlder = true;
  try {
    // 1. Snapshot the scroll anchor: the topmost message currently
    //    visible in the scroll container. We deliberately pick the
    //    first message whose top edge is at or below the viewport top
    //    (scrollTop), so the anchor is on-screen even if a tall
    //    message spans the fold. Fall back to the absolute first row
    //    if none match (we are at the very top of the thread).
    const anchor = snapshotScrollAnchor(app);

    // 2. Fetch the older page. The server applies its own dedupe /
    //    sanitisation, so we trust the result array shape.
    type OlderHistoryResult = {
      messages?: unknown[];
      oldestMessageId?: string;
      hasMoreOlder?: boolean;
    };
    const res = (await app.client.request("chat.history", {
      sessionKey: app.sessionKey.trim(),
      limit: HISTORY_PAGE,
      before: cursor,
    })) as OlderHistoryResult;

    // JAISIU-3473 — session-switch race guard.
    //
    // Snapshot the active sessionKey at call time. If the operator
    // switched chats while the RPC was in flight, the response
    // belongs to the OLD session and must NOT be prepended into the
    // new one. Bail out cleanly: release the in-flight flag, leave
    // the new chat's transcript untouched, leave the new chat's
    // cursor untouched. The mirror of this guard inside
    // `loadChatHistory` is the `chatHistoryGeneration` counter; the
    // cursor pager uses a simpler sessionKey equality check because
    // each pager call is a fresh RPC with no shared generation token.
    const incoming = Array.isArray(res.messages) ? res.messages : [];
    if (incoming.length === 0) {
      // Server said there is nothing older (or cursor was stale and
      // resolved to the empty prefix). Disarm the pager so subsequent
      // scroll-up events do not fire.
      app.chatHistoryHasMoreOlder = false;
      app.chatHistoryOldestLoadedId = res.oldestMessageId ?? null;
      return;
    }
    // Re-check sessionKey AFTER the await. If the operator switched
    // chats while the RPC was in flight, drop the response on the
    // floor. We deliberately do NOT touch `chatMessages`,
    // `chatHistoryOldestLoadedId`, or `chatHistoryHasMoreOlder` —
    // those belong to the NEW chat now.
    if ((app.sessionKey ?? "").trim() !== requestedSessionKey) {
      return;
    }

    // 3. Dedupe by id against the existing transcript. The server may
    //    echo optimistic user messages that we have already rendered;
    //    the client-side dedupe keeps the array stable.
    const existing = Array.isArray(app.chatMessages) ? app.chatMessages : [];
    const existingIds = new Set<string>();
    for (const m of existing) {
      const id = getMessageId(m);
      if (id) existingIds.add(id);
    }
    const fresh: unknown[] = [];
    for (const m of incoming) {
      const id = getMessageId(m);
      if (id && existingIds.has(id)) continue;
      fresh.push(m);
    }
    if (fresh.length === 0) {
      // Nothing new after dedupe (race with a concurrent history poll).
      // Honor the server's `oldestMessageId` and `hasMoreOlder` as the
      // authoritative state so we don't loop forever.
      app.chatHistoryOldestLoadedId = res.oldestMessageId ?? null;
      app.chatHistoryHasMoreOlder = res.hasMoreOlder === true;
      return;
    }

    // 4. Prepend. Order: chronological ASCENDING. `fresh` is already
    //    in ascending order (server returns ascending within the page).
    //    After prepend, the new array is [...fresh, ...existing].
    app.chatMessages = [...fresh, ...existing];

    // 5. Update cursor. `oldestMessageId` is the server-pinned cursor
    //    for the OLDEST message in the page we just prepended; send
    //    it back next time.
    app.chatHistoryOldestLoadedId = res.oldestMessageId ?? getMessageId(fresh[0]) ?? null;
    app.chatHistoryHasMoreOlder = res.hasMoreOlder === true;

    // 6. Restore scroll position. After Lit re-renders the transcript,
    //    the anchor row's offsetTop has grown by the total height of
    //    the prepended messages. We compute the delta from
    //    `fresh.length` (each row roughly contributes its rendered
    //    height; the precise anchor lookup gives the exact delta),
    //    then apply the adjustment in a guarded block so the scroll
    //    handler does not see the resulting `scroll` event as a user
    //    gesture.
    if (anchor) {
      await restoreScrollAnchor(app, anchor);
    }
  } catch (err) {
    // JAISIU-3473 + JAISIU-3536 — loading failure must NOT destroy
    // the visible conversation. Surface the error both to lastError
    // (legacy path for chat-send errors) and to chatHistoryError
    // (JAISIU-3536 non-blocking toast with Retry affordance) and
    // keep the existing transcript + cursor intact so the operator
    // can retry by scrolling up again.
    try {
      app.lastError = String(err);
    } catch {
      // no-op: lastError may be readonly in some test hosts.
    }
    try {
      app.chatHistoryError = {
        direction: "older",
        cursorKey: cursor ?? "",
        message: String(err),
        at: Date.now(),
      };
    } catch {
      /* no-op */
    }
  } finally {
    app.chatHistoryLoadingOlder = false;
  }
}

type ScrollAnchor = {
  msgId: string;
  /** offsetTop of the anchor row at fetch time (relative to the scroll container). */
  offsetTop: number;
  /** scrollTop of the container at fetch time. */
  scrollTop: number;
  /** Height of the anchor row at fetch time (sanity-check for variable heights). */
  offsetHeight: number;
};

function snapshotScrollAnchor(app: OpenClawApp): ScrollAnchor | null {
  const container = pickScrollContainer(app);
  if (!container) return null;
  const scrollTop = container.scrollTop;
  // Find the first row whose top is at or below the viewport top. This
  // is the row whose visual position we want to keep stable. If the
  // operator is scrolled to the very top, the first row qualifies.
  const rows = container.querySelectorAll<HTMLElement>(CHAT_MSG_ID_SELECTOR);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.offsetTop >= scrollTop - 1) {
      const id = row.getAttribute("data-msg-id");
      if (id) {
        return {
          msgId: id,
          offsetTop: row.offsetTop,
          scrollTop,
          offsetHeight: row.offsetHeight,
        };
      }
    }
  }
  return null;
}

async function restoreScrollAnchor(app: OpenClawApp, anchor: ScrollAnchor): Promise<void> {
  // Lit needs a microtask to commit the prepended DOM. Wait one
  // animation frame after `updateComplete` so the new rows are in
  // the layout tree (and `offsetTop` is stable).
  try {
    await app.updateComplete;
  } catch {
    /* updateComplete may reject if app is being torn down; skip. */
  }
  const container = pickScrollContainer(app);
  if (!container) return;
  const row = container.querySelector<HTMLElement>(`[data-msg-id="${cssEscape(anchor.msgId)}"]`);
  // Compute the exact delta: new offsetTop - old offsetTop.
  // When the anchor row exists, this is the height of everything that
  // was prepended above it (the cumulative height of `fresh` rows).
  // When the row is missing (rare — e.g. the anchor message was
  // filtered out by sanitisation on this render), fall back to the
  // math: shift scrollTop by the difference between the container's
  // scrollHeight now vs at fetch time.
  let delta: number;
  if (row) {
    delta = row.offsetTop - anchor.offsetTop;
  } else {
    delta = container.scrollHeight - (anchor.scrollTop + container.clientHeight);
    if (delta < 0) delta = 0;
  }
  // Bracket the adjustment with the programmatic-scroll flag so the
  // scroll handler does not treat the resulting `scroll` event as a
  // user gesture. JAISIU-3502 — without this guard, the post-prepend
  // scrollTop write would feed the up/down accumulator and could
  // arm either chevron at exactly the wrong moment.
  // JAISIU-3496 / G3 — bracket via `withProgrammaticScroll` so the
  // flag is guaranteed to be cleared even if a rAF below throws.
  // See `with-programmatic-scroll.ts` for the contract.
  await withProgrammaticScroll(app, async () => {
    const raf = new Promise<void>((resolve) =>
      requestAnimationFrame(() => {
        container.scrollTop = anchor.scrollTop + delta;
        resolve();
      }),
    );
    await raf;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  });
}

function cssEscape(value: string): string {
  // Minimal CSS.escape polyfill for environments where the global is
  // unavailable (e.g. some jsdom test runners). The id format we use
  // (`m-XXXX` / `msg-...`) is already CSS-safe, so this only needs to
  // handle a narrow set of edge cases.
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}

function pickScrollContainer(app: OpenClawApp): HTMLElement | null {
  // Mirrors `pickChatScrollTarget` in app-scroll.ts; intentionally a
  // local helper so this module does not depend on the scroll handler
  // (and vice-versa).
  const selectors = [".chat-fleet-main-scroll", ".chat-thread-scroll", ".chat-thread"];
  for (const selector of selectors) {
    const node = app.querySelector?.(selector) ?? null;
    if (!node) continue;
    const container = node as HTMLElement;
    const canScroll = container.scrollHeight - container.clientHeight > 1;
    if (canScroll) return container;
  }
  return null;
}

/**
 * JAISIU-3536 — deterministic transcript dedupe by stable `messageId`.
 *
 * Order is preserved by the first-seen position of each id. The merge
 * result is deterministic: same input + same existing transcript → same
 * output. Used by:
 *   - loadOlderChatHistory (prepend direction)
 *   - loadNewerChatHistory (append direction)
 *   - centered-restore (`loadChatHistory({ around })`)
 *   - polling refresh (JAISIU-3535)
 *   - live event arrival (streaming)
 *
 * Never dedupe by index, hash of content, or timestamp — only the
 * server-issued `id` is stable.
 */
export function mergeByMessageId(
  existing: unknown[],
  incoming: unknown[],
): { merged: unknown[]; dropped: number } {
  const seen = new Set<string>();
  const merged: unknown[] = [];
  let dropped = 0;
  for (const m of existing) {
    const id = getMessageId(m);
    if (id) {
      if (seen.has(id)) {
        dropped += 1;
        continue;
      }
      seen.add(id);
    }
    merged.push(m);
  }
  for (const m of incoming) {
    const id = getMessageId(m);
    if (id) {
      if (seen.has(id)) {
        dropped += 1;
        continue;
      }
      seen.add(id);
    }
    merged.push(m);
  }
  return { merged, dropped };
}

/**
 * JAISIU-3536 — page newer history (downward direction). Only fires
 * when a saved anchor exists for the active session AND there are
 * messages below the rendered window. The guard short-circuits
 * otherwise — the newer direction is normally owned by polling /
 * streaming (JAISIU-3535), so this path must never run in the
 * newest-at-bottom case.
 *
 * Idempotent: `OpenClawApp.loadNewerChatHistory` wraps this with an
 * in-flight promise cache so duplicate calls within <50 ms return the
 * same promise — covered by `chat-history-dedupe.node.test.ts`.
 *
 * On error: the existing transcript and viewport are unchanged; a
 * non-blocking error toast is surfaced on `app.chatHistoryError`.
 */
export async function loadNewerChatHistory(app: OpenClawApp): Promise<void> {
  if (app.chatHistoryLoadingNewer) return;
  if (!app.client || !app.connected) return;
  // JAISIU-3532 — newer paging is gated on a saved anchor. Without
  // one, the newest-at-bottom case is canonical and polling owns the
  // arrival of new messages.
  const anchor = app.chatScrollAnchor;
  if (!anchor || anchor.sessionKey !== (app.sessionKey ?? "").trim()) {
    return;
  }
  const requestedSessionKey = (app.sessionKey ?? "").trim();
  app.chatHistoryLoadingNewer = true;
  try {
    const res = (await app.client.request("chat.history", {
      sessionKey: requestedSessionKey,
      limit: HISTORY_PAGE,
      after: app.chatHistoryNewestLoadedId ?? undefined,
    })) as {
      messages?: unknown[];
      newestMessageId?: string;
      hasMoreNewer?: boolean;
    };
    const incoming = Array.isArray(res.messages) ? res.messages : [];
    if (incoming.length === 0) {
      app.chatHistoryHasMoreNewer = false;
      app.chatHistoryNewestLoadedId = res.newestMessageId ?? null;
      return;
    }
    if ((app.sessionKey ?? "").trim() !== requestedSessionKey) {
      return;
    }
    // JAISIU-3536 — deterministic dedupe, append direction.
    const existing = Array.isArray(app.chatMessages) ? app.chatMessages : [];
    const { merged, dropped } = mergeByMessageId(existing, incoming);
    if (dropped > 0 || merged.length !== existing.length) {
      app.chatMessages = merged;
    }
    app.chatHistoryNewestLoadedId =
      res.newestMessageId ?? getMessageId(incoming[incoming.length - 1]) ?? null;
    app.chatHistoryHasMoreNewer = res.hasMoreNewer === true;
  } catch (err) {
    // JAISIU-3536 — error preservation. The transcript + viewport are
    // untouched; surface a non-blocking toast with retry affordance.
    try {
      app.chatHistoryError = {
        direction: "newer",
        cursorKey: app.chatHistoryNewestLoadedId ?? "",
        message: String(err),
        at: Date.now(),
      };
    } catch {
      /* no-op */
    }
  } finally {
    app.chatHistoryLoadingNewer = false;
  }
}

export function populatePendingReviewFromSummary(app: OpenClawApp, changeIds: string[]): void {
  app.chatPendingReviewChangeIds = changeIds.length > 0 ? [...new Set(changeIds)] : [];
}

export async function refreshChatCheckpoints(app: OpenClawApp): Promise<void> {
  if (!app.client || !app.connected) {
    app.chatCheckpoints = [];
    return;
  }
  app.chatCheckpointsLoading = true;
  try {
    app.chatCheckpoints = await fetchArtifactCheckpoints(app.client, {
      sessionKey: app.sessionKey,
      projectId: app.chatActiveProjectId,
      limit: 15,
    });
  } catch {
    app.chatCheckpoints = [];
  } finally {
    app.chatCheckpointsLoading = false;
  }
}

export async function handleRestoreCheckpoint(app: OpenClawApp, sinceTs: number): Promise<void> {
  if (!app.client) {
    return;
  }
  app.chatArtifactReviewBusy = true;
  try {
    await revertArtifactsSince(app.client, {
      sessionKey: app.sessionKey,
      sinceTs,
      projectId: app.chatActiveProjectId,
    });
    await refreshChatCheckpoints(app);
    app.chatPendingReviewChangeIds = [];
  } finally {
    app.chatArtifactReviewBusy = false;
  }
}

export function handleAcceptArtifactChange(app: OpenClawApp, changeId: string): void {
  app.chatPendingReviewChangeIds = app.chatPendingReviewChangeIds.filter((id) => id !== changeId);
  if (app.chatPendingReviewChangeIds.length === 0) {
    dismissComposerFileReview(app);
    void refreshChatCheckpoints(app);
    void app.refreshChatWorkspace?.();
    return;
  }
  advanceComposerReview(app);
}

export function handleComposerReviewAll(app: OpenClawApp): void {
  const sessionKey = app.sessionKey.trim();
  const snapshot = resolveChatLastRunSnapshot(app, sessionKey);
  if (!snapshot?.files.length) {
    app.chatComposerFileReviewError = "No file changes to review.";
    return;
  }
  app.chatComposerFileReviewError = null;
  if (app.chatPendingReviewChangeIds.length === 0) {
    populatePendingReviewFromSummary(
      app,
      snapshot.files.map((f) => f.changeId).filter((id): id is string => Boolean(id)),
    );
  }
  advanceComposerReview(app);
}

export function handleComposerKeepAll(app: OpenClawApp): void {
  dismissComposerFileReview(app);
  void refreshChatCheckpoints(app);
  void app.refreshChatWorkspace?.();
}

export async function handleComposerUndoAll(app: OpenClawApp): Promise<void> {
  if (!app.client) {
    app.chatComposerFileReviewError = "Not connected to gateway.";
    return;
  }
  const sessionKey = app.sessionKey.trim();
  const snapshot = resolveChatLastRunSnapshot(app, sessionKey);
  if (!snapshot?.files.length) {
    app.chatComposerFileReviewError = "No file changes to undo.";
    return;
  }
  app.chatArtifactReviewBusy = true;
  app.chatComposerFileReviewError = null;
  try {
    const sinceTs = getChatRunArtifactsSinceTs(app.chatRunArtifactsSinceTs, sessionKey);
    let reverted = 0;
    const failed: string[] = [];

    if (sinceTs != null) {
      for (const scopeKey of collectArtifactScopeSessionKeys(app, snapshot.files)) {
        const batch = await revertArtifactsSince(app.client, {
          sessionKey: scopeKey,
          sinceTs,
          projectId: app.chatActiveProjectId,
        });
        reverted += batch.reverted;
        failed.push(...batch.failed);
      }
    }

    if (reverted === 0) {
      const batch = await revertSnapshotFiles(app.client, snapshot.files, {
        mainSessionKey: sessionKey,
        projectId: app.chatActiveProjectId,
      });
      reverted = batch.reverted;
      failed.push(...batch.failed);
    }

    if (reverted === 0) {
      app.chatComposerFileReviewError =
        failed.length > 0 ? `Could not undo changes: ${failed[0]}` : "Could not undo changes.";
      return;
    }

    dismissComposerFileReview(app);
    app.chatRunArtifactsSinceTs = clearChatRunArtifactsBaseline(
      app.chatRunArtifactsSinceTs,
      sessionKey,
    );
    await refreshChatCheckpoints(app);
    await loadChatHistory(app);
    void app.refreshChatWorkspace?.();
  } finally {
    app.chatArtifactReviewBusy = false;
  }
}

export async function handleRejectArtifactChange(
  app: OpenClawApp,
  changeId: string,
): Promise<void> {
  if (!app.client) {
    return;
  }
  const scopeKey =
    app.sessionArtifactDetail?.change.sessionKey?.trim() ||
    app.sessionInspectorFilesScopeKey?.trim() ||
    app.sessionKey.trim();
  app.chatArtifactReviewBusy = true;
  try {
    const ok = await revertArtifactChange(app.client, {
      sessionKey: scopeKey,
      changeId,
      projectId: app.chatActiveProjectId,
    });
    if (!ok) {
      app.sessionArtifactsError = "Could not revert this file change.";
      return;
    }
    app.chatPendingReviewChangeIds = app.chatPendingReviewChangeIds.filter((id) => id !== changeId);
    if (app.chatPendingReviewChangeIds.length === 0) {
      dismissComposerFileReview(app);
      await refreshChatCheckpoints(app);
      void app.refreshChatWorkspace?.();
      return;
    }
    if (app.chatFileDiffOpen?.changeId === changeId) {
      const { closeChatFileDiff } = await import(/* @vite-ignore */ "./app-file-diff.ts");
      closeChatFileDiff(app);
    }
    await refreshChatCheckpoints(app);
    void app.refreshChatWorkspace?.();
    advanceComposerReview(app);
  } finally {
    app.chatArtifactReviewBusy = false;
  }
}

export async function handleOpenArtifactInProjectFiles(
  app: OpenClawApp,
  path: string,
  artifactSessionKey?: string,
): Promise<void> {
  const trimmed = path.trim();
  if (!trimmed) {
    return;
  }
  app.handleSessionEnvironmentOpenWorkspace();
  await app.refreshChatWorkspace?.();

  const { findFleetFileUiPath, fleetUiPathAncestorDirs } = await import(
    /* @vite-ignore */ "./chat/fleet-session-files-tree.ts"
  );
  const uiPath = findFleetFileUiPath(app.chatWorkspaceFileTargets, {
    relativePath: trimmed,
    sessionKey: artifactSessionKey,
  });

  if (typeof document !== "undefined") {
    document.getElementById("fleet-bottom-project-files")?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }

  if (!uiPath) {
    app.chatWorkspaceFileError = `File not found in workspace tree: ${trimmed}. Try Refresh on the Project files panel.`;
    app.chatWorkspaceSelectedPath = null;
    app.chatWorkspaceFileContent = null;
    return;
  }

  const expanded = { ...app.chatWorkspaceExpandedDirs };
  for (const dir of fleetUiPathAncestorDirs(uiPath)) {
    expanded[dir] = true;
  }
  app.chatWorkspaceExpandedDirs = expanded;
  await app.openChatWorkspaceFile(uiPath);
}

export async function handleFleetTodoStatusChange(
  app: OpenClawApp,
  id: string,
  status: FleetTodoStatus,
): Promise<void> {
  const sessionKey = app.sessionKey.trim();
  if (!app.client || !sessionKey) {
    return;
  }
  const todos = parseFleetTodoItems(app.fleetTodosBySession[sessionKey] ?? []);
  const item = todos.find((t) => t.id === id);
  if (!item) {
    return;
  }
  app.fleetTodosBusy = true;
  try {
    const res = await app.client.request<{ todos: unknown[] }>("fleet.todos.update", {
      sessionKey,
      id,
      status,
    });
    if (res?.todos) {
      setFleetTodosForSession(app, sessionKey, parseFleetTodoItems(res.todos));
    }
  } catch {
    /* ignore */
  } finally {
    app.fleetTodosBusy = false;
  }
}

export function handleFileContextSelect(app: OpenClawApp, path: string): void {
  const draft = app.chatMessage;
  const token = formatFileContextToken(path);
  if (/@file:[^\s]*$/.test(draft)) {
    app.chatMessage = draft.replace(/@file:[^\s]*$/, token);
  } else {
    app.chatMessage = `${draft}${draft.endsWith(" ") || draft.length === 0 ? "" : " "}${token}`;
  }
  app.chatFileContextIndex = 0;
  syncChatComposeDraft(app.chatMessage);
}

export function handleFolderContextSelect(app: OpenClawApp, path: string): void {
  const draft = app.chatMessage;
  const token = formatFolderContextToken(path);
  if (/@(?:folder|dir):[^\s]*$/.test(draft)) {
    app.chatMessage = draft.replace(/@(?:folder|dir):[^\s]*$/, token);
  } else {
    app.chatMessage = `${draft}${draft.endsWith(" ") || draft.length === 0 ? "" : " "}${token}`;
  }
  app.chatFolderContextIndex = 0;
  syncChatComposeDraft(app.chatMessage);
}

export function resolveActivityAgentId(sessionKey: string): string {
  return resolveAgentIdForSession(sessionKey);
}

/**
 * JAISIU-3473 — drain the older-history backlog into the DOM.
 *
 * Loops `loadOlderChatHistory` until the server reports no more pages.
 * Intended for the in-thread search entry path: when the operator
 * enters a query, the view's filter only sees the rendered window, so
 * older matches would be invisible. The drain brings every older page
 * into the DOM in the background; the operator can keep scrolling
 * and the indicator continues to show while the loop runs.
 *
 * Safe to invoke repeatedly. Concurrent invocations coalesce: the
 * in-flight guard inside `loadOlderChatHistory` prevents duplicate
 * RPCs, so a second caller will be a no-op until the first completes.
 */
export async function drainOlderChatHistory(app: OpenClawApp): Promise<void> {
  let safety = 0;
  // Bound the loop so a misbehaving server cannot loop forever. The
  // server caps each page at `limit` (200), so 10_000 pages covers a
  // 2M-message transcript — well beyond any realistic chat.
  const HARD_CAP = 10_000;
  while (app.chatHistoryHasMoreOlder && !app.chatHistoryLoadingOlder && safety < HARD_CAP) {
    safety += 1;
    await loadOlderChatHistory(app);
  }
}

/**
 * JAISIU-3474 — restore the operator's last-viewed scroll position
 * after a browser refresh.
 *
 * Lifecycle:
 *   1. Caller (the app bootstrap in `app-lifecycle.ts`) awaits the
 *      initial `loadChatHistory` so the latest-N window is in
 *      `chatMessages`.
 *   2. If the persisted anchor message id is in that window, we
 *      commit a `scrollTop` write that lands the row at the saved
 *      offset-within-message. The write is bracketed by
 *      `chatProgrammaticScroll` so the scroll handler does not
 *      classify it as a user gesture (preserves the JAISIU-3502
 *      chevron contract and the JAISIU-3473 pager contract).
 *   3. If the anchor is NOT in the latest-N window, we drain older
 *      pages via `loadOlderChatHistory` until the anchor id appears
 *      OR the server reports no more older content. Then restore as
 *      in step 2.
 *   4. The `chatScrollRestorationPending` flag is cleared on success
 *      and on graceful no-op exits. A failed restore (anchor row
 *      never materialises, e.g. a deleted message) is treated as a
 *      no-op so the operator lands at the bottom and the indicator
 *      goes away.
 *
 * The function is idempotent: subsequent calls during the same
 * session are no-ops because `chatScrollRestorationPending` is
 * cleared at the end of the first successful run. This makes it
 * safe to invoke from both `firstUpdated` and the `connected`
 * watcher without risking a double-restore.
 *
 * Safety: a misbehaving server cannot loop forever — we bound the
 * drain at `RESTORE_DRAIN_HARD_CAP` pages. A 2M-message transcript
 * (way beyond any realistic chat) exhausts 10_000 × 200-message
 * pages long before the operator notices anything wrong.
 */
export async function restorePersistedScrollAnchor(app: OpenClawApp): Promise<void> {
  // Guard 1 — a restore is already pending or has run; nothing to do.
  if (!app.chatScrollRestorationPending) return;
  const anchor = app.chatScrollAnchor;
  if (!anchor) {
    app.chatScrollRestorationPending = false;
    return;
  }
  // Guard 2 — the saved anchor was scoped to a different session.
  // This is belt-and-braces: the loader only ever sets the field
  // for the active session, but a session switch mid-bootstrap could
  // in theory race.
  const requestedSessionKey = (app.sessionKey ?? "").trim();
  if (!requestedSessionKey || anchor.sessionKey !== requestedSessionKey) {
    app.chatScrollRestorationPending = false;
    return;
  }
  // Guard 3 — prerequisites. We need a client (for older-page drains),
  // a connected WS, and a non-empty transcript to anchor against. A
  // missing client or empty transcript means the bootstrap is mid-
  // flight; clear the pending flag so the next call (after the WS
  // connects and the history lands) re-arms the restore. Without
  // this, a stuck `chatScrollRestorationPending=true` would prevent
  // every subsequent restore attempt.
  if (!app.client || !app.connected) {
    app.chatScrollRestorationPending = false;
    return;
  }
  const messages = Array.isArray(app.chatMessages) ? app.chatMessages : [];
  if (messages.length === 0) {
    app.chatScrollRestorationPending = false;
    return;
  }
  // Track whether the anchor is already in the most recently
  // rendered transcript. When it IS, the row's offsetTop may still
  // differ from the captured session (different surrounding rows,
  // different recent-message fan-out), so we still run the centered
  // fetch below to land the row at a known position. When it is
  // NOT, the latest-N window does not have the row at all and the
  // centered fetch is the only way to bring it into the DOM.
  const anchorInWindow = chatMessagesContainsId(messages, anchor.messageId);
  // JAISIU-3533 — always run a centered `around=` fetch on restore.
  // The centered window places the anchor at index floor(limit/2) of
  // the returned slice, which means the anchor row lands at a known,
  // stable position in the new DOM regardless of how the latest-N
  // window would have rendered it. Without this, `row.offsetTop`
  // can differ by hundreds of pixels between the captured session
  // and the restored session (different rows above/below the anchor,
  // different scrollHeight, different recent-message fan-out), and
  // the relative `viewportOffset` math (`row.offsetTop +
  // viewportOffset`) puts the viewport somewhere the operator is not
  // reading — most often pinned to a row a few hundred px above the
  // anchor, so the user lands near the top of available chat history
  // instead of on the message they were reading.
  //
  // Skip the centered fetch only when the anchor is known to be the
  // very last message in the transcript (no newer neighbours): a
  // centered fetch would be wasted bandwidth because the anchor is
  // already pinned at scrollHeight in the latest-N window.
  try {
    const { loadChatHistory } = await import("./controllers/chat.ts");
    await loadChatHistory(app, { force: true, around: anchor.messageId });
    if ((app.sessionKey ?? "").trim() !== requestedSessionKey) {
      // Operator switched sessions during the centered fetch — abort.
      app.chatScrollRestorationPending = false;
      return;
    }
  } catch {
    // Fall through to the cursor-pager drain below so a degraded
    // server (RPC error, anchor deleted server-side) still has a
    // fallback path.
  }
  // Cursor-pager fallback / safety net. If the centered fetch landed
  // the anchor we skip the loop entirely; if it didn't (anchor
  // deleted), we drain until either we find the anchor or the server
  // says there is nothing older. Bound the loop so a deleted anchor +
  // a server that reports `hasMoreOlder=true` infinitely cannot wedge
  // the bootstrap.
  let safety = 0;
  const RESTORE_DRAIN_HARD_CAP = 200; // 200 pages × 200 msgs = 40k messages
  while (
    !chatMessagesContainsId(app.chatMessages, anchor.messageId) &&
    app.chatHistoryHasMoreOlder &&
    !app.chatHistoryLoadingOlder &&
    safety < RESTORE_DRAIN_HARD_CAP
  ) {
    safety += 1;
    await loadOlderChatHistory(app);
    if ((app.sessionKey ?? "").trim() !== requestedSessionKey) {
      // Operator switched sessions during the drain — abort.
      app.chatScrollRestorationPending = false;
      return;
    }
  }
  // Re-check post-drain: if the anchor still isn't here, bail.
  // JAISIU-3533 (operator repro 2026-10-11) — the previous behaviour
  // of "clear the pending flag and return, leaving the viewport at
  // scrollTop=0" caused the "stuck after refresh, only the last
  // messages visible, can't load when scrolling" symptom. Reasoning:
  // the operator lands at the TOP of the latest-N window. The
  // scroll-up pager only fires on an actual upward scroll event
  // (line 824 in app-scroll.ts: `scrollTop < AT_TOP_THRESHOLD`).
  // Pinned at scrollTop=0 the pager can never trigger, so the chat
  // looks frozen. The only escape was a hard refresh — exactly what
  // the operator reported.
  //
  // The fix: when the anchor is genuinely missing from the server's
  // transcript, treat the bail as a "no anchor to restore — show the
  // latest content" case. Place the viewport at the bottom of the
  // available transcript (scrollHeight - clientHeight) so:
  //   1. The operator sees the most recent messages (the canonical
  //      "fresh load" reading position).
  //   2. A wheel-up from the bottom is a real scroll gesture, so the
  //      upward pager becomes reachable and the operator can page
  //      older content if they want to.
  //   3. The post-write `scroll` event is bracketed by
  //      `chatProgrammaticScroll` so the scroll handler does not
  //      classify the jump as a user gesture (which would arm the
  //      ↓ chevron on a programmatic jump-to-bottom).
  if (!chatMessagesContainsId(app.chatMessages, anchor.messageId)) {
    const fallbackContainer = pickScrollContainer(app);
    if (fallbackContainer) {
      const fallbackTarget = Math.max(
        0,
        fallbackContainer.scrollHeight - fallbackContainer.clientHeight,
      );
      // Only write if we are not already there. The 1-px tolerance
      // matches the existing skip-already-at-target guard at line
      // 1000 — avoids a redundant `scroll` event that would
      // otherwise need the scroll handler to evaluate.
      if (Math.abs(fallbackContainer.scrollTop - fallbackTarget) > 1) {
        try {
          await withProgrammaticScroll(app, async () => {
            fallbackContainer.scrollTop = fallbackTarget;
            // Two rAFs so the scroll handler observes the flag set
            // BEFORE the resulting `scroll` event fires (same
            // contract as the successful-restore bracket at line
            // 1023).
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => resolve()),
            );
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => resolve()),
            );
          });
        } catch {
          // Scroll write is best-effort on the bail path. A throw
          // (container detached, layout reflow failure) must not
          // wedge the pending flag — clear it and move on. The
          // chat is still readable; the pager is still reachable
          // from any subsequent user gesture.
        }
      }
    }
    app.chatScrollRestorationPending = false;
    return;
  }
  // Wait for the post-centered-fetch DOM to settle before measuring
  // offsets. The spec mandates "wait until the restored messages have
  // their final layout/height" — a single Lit commit is not always
  // enough because images, code blocks, and streaming-token placeholders
  // resolve their final height over multiple animation frames. We await
  // `app.updateComplete` and then two extra rAFs as a robust settle
  // window, AND we re-measure the row height one final time inside the
  // bracket so any subsequent reflow is caught.
  try {
    await app.updateComplete;
  } catch {
    /* host tearing down — skip */
    return;
  }
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  // JAISIU-3533-update — bump the transcriptRevision on the host so
  // JAISIU-3535's drift assertion has a stable baseline. The counter
  // increments once per restore, never on a poll or stream event, so
  // "no override after restore" is straightforward to test for.
  if (
    typeof (app as unknown as { chatScrollTranscriptRevision?: string })
      .chatScrollTranscriptRevision === "string"
  ) {
    (app as unknown as { chatScrollTranscriptRevision: string }).chatScrollTranscriptRevision =
      String(Date.now());
  }
  const container = pickScrollContainer(app);
  if (!container) {
    app.chatScrollRestorationPending = false;
    return;
  }
  const row = container.querySelector<HTMLElement>(
    `[data-msg-id="${cssEscapeAnchor(anchor.messageId)}"]`,
  );
  if (!row) {
    app.chatScrollRestorationPending = false;
    return;
  }
  // JAISIU-3533-update — restore the operator's READING POSITION,
  // not just the top of the anchor row. We compute where the
  // viewport CENTER was relative to the captured session, then
  // mirror that onto the post-restore DOM.
  //
  // Two anchors are stored in IDB (see `captureScrollAnchor` in
  // app-scroll.ts + `idb-session-cache.ts`):
  //
  //   `positionRatio`  (0..1)   — fractional position of the viewport
  //                              center within the anchor row at
  //                              capture. The preferred signal; we
  //                              compute `desiredCenter =
  //                              row.offsetTop + positionRatio *
  //                              row.offsetHeight` (after the post-
  //                              centered-fetch Lit commit so the
  //                              row has its final height) and place
  //                              it at the new viewport center.
  //   `sectionIndex`   (0..4)   — 5-bucket quantisation. Used as a
  //                              fallback when `positionRatio` is
  //                              missing or when the restored row
  //                              height differs from capture by more
  //                              than a small threshold (a streaming
  //                              message that has not finished, an
  //                              unloaded image, etc.) — in which
  //                              case the floating ratio can land
  //                              the viewport at a sub-pixel
  //                              position inside an unrendered
  //                              region. The bucket boundary at the
  //                              center is a stable coarse target.
  //
  // The desired CENTER is then turned into a scrollTop via
  // `target = desiredCenter - clientHeight / 2`, clamped to the
  // container's scrollable range so a stale offset (different
  // viewport size, different surrounding rows) cannot push the
  // viewport past the bottom or beyond the document top.
  const rowHeight = Math.max(1, row.offsetHeight || 1);
  // JAISIU-types-sweep: `IdbScrollAnchorEntry` now declares
  // `positionRatio` and `sectionIndex` as optional (the load-path
  // coalesces missing values to defaults, but the runtime shape from
  // older IDB records may still see `undefined`). Guard each branch
  // explicitly with an `undefined` check before the range test.
  const ratio =
    typeof anchor.positionRatio === "number" &&
    Number.isFinite(anchor.positionRatio) &&
    anchor.positionRatio >= 0 &&
    anchor.positionRatio <= 1
      ? anchor.positionRatio
      : null;
  const sectionRatio =
    typeof anchor.sectionIndex === "number" &&
    Number.isFinite(anchor.sectionIndex) &&
    anchor.sectionIndex >= 0 &&
    anchor.sectionIndex <= 4
      ? anchor.sectionIndex / 4
      : null;
  // Clamp the ratio to [0, 1] before use. We deliberately accept
  // any non-negative finite number at the load step and clamp here
  // rather than rejecting out-of-range ratios: the cap of 1.0 means
  // "row bottom at the viewport center", which is a sensible
  // fallback for legacy IDB records that stored weird values, and
  // the bottom-edge clamp on `target` (maxScroll) prevents the
  // viewport from ever being pushed past the bottom.
  const effectiveRatio =
    ratio != null ? Math.max(0, Math.min(1, ratio)) : sectionRatio != null ? sectionRatio : 0.5; // absolute fallback — mid-message for an unknown anchor
  const desiredCenter = row.offsetTop + effectiveRatio * rowHeight;
  const newClientHeight = container.clientHeight || 0;
  const rawTarget = desiredCenter - newClientHeight / 2;
  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
  const target = Math.max(0, Math.min(rawTarget, maxScroll));
  // JAISIU-3533-update — skip the scroll write when we are already at
  // the target (within a small tolerance). The cache-hit pre-paint
  // path in `activateChatSession` (app.ts) places the viewport at
  // the anchor position synchronously so the operator never sees
  // "top of transcript → jump to anchor" on a session switch. If
  // the post-centered-fetch DOM has not shifted the row, the
  // pre-painted scrollTop is already the target and re-writing it
  // would either be a no-op (good) or trigger a second scroll event
  // (bad — the chevron-arm gate would have to re-evaluate). Bail
  // early on a 1-px tolerance so the operator sees a single,
  // smooth landing.
  if (Math.abs(container.scrollTop - target) <= 1) {
    app.chatScrollRestorationPending = false;
    return;
  }
  if (typeof console !== "undefined" && (import.meta as { env?: { DEV?: boolean } })?.env?.DEV) {
    // Diagnostic surface so we can spot fallbacks in development.
    const ratioSource =
      ratio != null ? "positionRatio" : sectionRatio != null ? "sectionIndex" : "fallback";
    // eslint-disable-next-line no-console
    console.debug(
      `[JAISIU-3533] restore via ${ratioSource} ratio=${effectiveRatio.toFixed(3)} ` +
        `row=${(row.getAttribute("data-msg-id") || "").slice(0, 8)} top=${row.offsetTop} h=${rowHeight} ` +
        `desiredCenter=${desiredCenter.toFixed(0)} clientHeight=${newClientHeight} ` +
        `target=${target.toFixed(0)} maxScroll=${maxScroll.toFixed(0)}`,
    );
  }
  // JAISIU-3496 / G3 — bracket via `withProgrammaticScroll` so the
  // flag is guaranteed to be cleared even if a rAF below throws.
  // We hold the flag for two animation frames: one to let the browser
  // fire the scroll event AFTER the write, one to let the scroll
  // handler run on the next frame and observe the flag set BEFORE
  // the scroll event reaches it. See `with-programmatic-scroll.ts`
  // for the contract.
  await withProgrammaticScroll(app, async () => {
    container.scrollTop = target;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  });
  // Clear the pending flag. We deliberately do NOT clear
  // `chatScrollAnchor` itself — leaving the in-memory mirror in
  // place lets the next scroll event overwrite it correctly without
  // a flash of empty anchor between this restore and the operator's
  // next gesture.
  app.chatScrollRestorationPending = false;

  // JAISIU-3535 — debug-only drift assertion. If a subsequent poll,
  // event, or initial render clobbers the restored position, we want
  // to know about it in development so a regression can be caught
  // before the operator sees it. The check is a console.warn, not a
  // throw, so production is unaffected.
  if (import.meta.env?.DEV) {
    setTimeout(() => {
      const c = pickScrollContainer(app);
      if (!c) return;
      const liveAnchor = app.chatScrollAnchorBySession?.get(requestedSessionKey);
      if (!liveAnchor) return;
      const liveRow = c.querySelector?.(`[data-msg-id="${cssEscape(liveAnchor.messageId)}"]`);
      if (!liveRow) return;
      const expectedTop = (liveRow as HTMLElement).offsetTop - liveAnchor.viewportOffset;
      if (Math.abs(c.scrollTop - expectedTop) > 2) {
        console.warn(
          `[JAISIU-3535] scroll drift after restore: expected ${expectedTop}, got ${c.scrollTop} (delta=${c.scrollTop - expectedTop})`,
        );
      }
    }, 100);
  }
}

/**
 * Linear scan for the anchor id inside the rendered transcript.
 * O(n) in the size of the rendered window which is bounded by the
 * cursor pager (max ~1000 messages in a single render); fine for
 * a one-shot restore.
 */
function chatMessagesContainsId(messages: unknown[], id: string): boolean {
  for (const message of messages) {
    if (!message || typeof message !== "object") continue;
    const record = message as { id?: unknown; transcriptId?: unknown; messageId?: unknown };
    for (const key of ["id", "transcriptId", "messageId"] as const) {
      const value = record[key];
      if (typeof value === "string" && value.trim() === id) return true;
    }
  }
  return false;
}

function cssEscapeAnchor(value: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(value);
  }
  return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => "\\" + ch);
}
